import {
    BUMP_ANIMATION_TIMEOUT,
    DEFAULT_SLIDE_TO_PAY_PREFIX,
    INSTANT_PAYMENT_FOP_LIST,
    INSTANT_PAYMENT_SUBTEXT_MAX_IMPRESSION_COUNT,
    INSTANT_PAYMENT_SUBTEXT_SHOWN,
    PAY_USING,
    SLIDE_BUMP_ANIMATION_SHOWN,
    SLIDE_TO_PAY_AMOUNT,
    SLIDE_TO_PAY_PREFIX,
    SLIDE_UPDATE_THROTTLE_TIME,
} from "@payment-ui/constants/TurboPayment";
// interfaces
import {
    CardPaymentMethodData,
    ITurboPaymentMethodProps,
    TurboCodPaymentMethodMeta,
    WalletPaymentGroupActions,
} from "@payment-ui/interfaces";
// styled-components
import {
    Container,
    DisplayNameContainer,
    KfsDisclaimerContainerV1,
    KfsLinkV1,
    PaymentMethodContainer,
    PaymentMethodSlideButtonContainer,
    PrimaryContainer,
} from "./styles";
import { LocalStorage, SlideButton, format, useEffectOnce } from "@swiggy-private/core-ui";
// internal-deps
import { updateFrequencyCount, useSessionStorage } from "@swiggy-private/checkout-core";
// deps
import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { getCardPaymentMethodExtraParams, isCardPaymentMethod, isFoodCard } from "@payment-ui/helpers/Card";
import {
    getInAppPaymentMethodExtraParams,
    isInAppUpiLitePaymentMethod,
    isInAppUpiPaymentMethod,
} from "@payment-ui/helpers/InAppUpi";
import { CappingModes } from "@swiggy-private/native-bridge";
import { getWalletPaymentMethodExtraParams, isWalletPaymentMethod } from "@payment-ui/helpers/Wallet";
import { isEmpty, throttle } from "@swiggy-private/js-utils";

import { ChangePaymentCta } from "@payment-ui/widgets/change-payment-cta";
import { ChangePaymentCtaV2 } from "@payment-ui/widgets/change-payment-cta-v2";
// widgets/components
import { TurboStrip } from "@swiggy-private/core-ui";
import { CvvBottomSheet } from "@payment-ui/components/cvv-bottom-sheet";
import { DEFAULT_CARD_DATA_STATE } from "@payment-ui/components/payment-method/card/constants";
// constants
import { PLACE_ORDER_EXTRA_PARAMS, TEXTS } from "@payment-ui/constants";
import PodConsentHalfCard from "@payment-ui/components/turbo-payment-widget/components/pod-half-card";
// helpers
import { SliderButtonContext } from "@payment-ui/components/turbo-payment-widget/SliderButtonContext";
import { TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS } from "@swiggy-private/payment-test-ids";
import { TurboPaymentContext } from "@payment-ui/components/turbo-payment-widget/TurboPaymentContext";
import TurboPaymentDisplayName from "../turbo-payment-display-name";
import { TurboPaymentMethodIcon } from "../turbo-payment-method-icon";
import TurboPaymentMethodMetaWidget from "../turbo-payment-method-meta-widget";
import { VALID_PAYMENT_METHODS } from "@swiggy-private/payment-types";
import { getCvvRequired } from "@payment-ui/helpers/Cvv";
import { getTurboPaymentPresentationalInfo } from "@payment-ui/helpers/TurboPayment";
import { isCODPaymentMethod } from "@payment-ui/helpers/Cod";
import { isAmazonPayLater } from "@payment-ui/helpers/PayLater";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

function TurboPaymentMethodWidget(props: ITurboPaymentMethodProps) {
    const {
        methodData,
        turboPaymentCtaMeta,
        paymentActions,
        onClickChangePaymentCta,
        balanceValue,
        showDiscountedAmount,
        turboPodHalfCardEvents,
        increaseSliderWidth,
        onClickTurboStrip,
        onImpressionTurboStrip,
        turboStrip = {},
        isTurboV2,
        clampDiscountText,
        keyFactStatementUrl,
        onKeyFactStatementClick,
    } = props;

    const {
        iconUrl,
        showPayUsingSuperScript,
        methodDisplayName,
        promoMsg,
        discount,
        balanceMeta,
        showInlineBalance,
        showNewLineBalance,
        cardSubName,
    } = getTurboPaymentPresentationalInfo(methodData, balanceValue);

    const toShowInstantPaymentSubtext = useMemo(() => {
        /* Instant Payment Subtext is to be shown beneath the Slider Text only for the cases
         * when the payment method is quick-payment and also the user is not seeing it more then 3 times */

        const groupName = methodData?.groupName?.toUpperCase();
        const isInstantPaymentFop =
            INSTANT_PAYMENT_FOP_LIST.includes(groupName) || isInAppUpiLitePaymentMethod(methodData); // Added separate check for UPI Lite since InAppUpi groupname is common for normal accounts and upi lite accounts
        const instantPaymentSubtextImpressionCount = LocalStorage.getItem(INSTANT_PAYMENT_SUBTEXT_SHOWN, 0) || 0;
        const toShowInstantPaymentSubtext =
            isInstantPaymentFop && instantPaymentSubtextImpressionCount < INSTANT_PAYMENT_SUBTEXT_MAX_IMPRESSION_COUNT;

        if (toShowInstantPaymentSubtext)
            LocalStorage.setItem(INSTANT_PAYMENT_SUBTEXT_SHOWN, instantPaymentSubtextImpressionCount + 1);

        return toShowInstantPaymentSubtext;
    }, [methodData]);

    const showTurboStrip = !isEmpty(turboStrip);

    const showKeyFactsDisclaimer = isAmazonPayLater(methodData.paymentCode) && !isEmpty(keyFactStatementUrl);

    const handleKeyFactsClick = useCallback(() => {
        if (keyFactStatementUrl && onKeyFactStatementClick) {
            onKeyFactStatementClick(keyFactStatementUrl);
        }
    }, [keyFactStatementUrl, onKeyFactStatementClick]);

    const [isBumpAnimationShown, setBumpAnimationShown] = useSessionStorage(SLIDE_BUMP_ANIMATION_SHOWN, false);
    const { onSlideButtonDrag, onSlideButtonSuccess, showChangeAnimation } = useContext(TurboPaymentContext);
    const { sliderButtonRef, onPostSliderSuccessCallback } = useContext(SliderButtonContext);
    const [showCvvPopup, setShowCvvPopup] = useState(false);
    const [showPodHalfCard, setPodHalfCard] = useState(false);

    useEffectOnce(() => {
        const timeout = setTimeout(() => {
            setBumpAnimationShown(true);
        }, BUMP_ANIMATION_TIMEOUT);

        return () => {
            clearTimeout(timeout);
        };
    });

    // We wont be showing CRED related offer on turbo widget for initial release and will be taken up in future
    // const dynamicPromoMsg = useMemo(() => {
    //     if (!isCredPaymentMethod(methodData)) {
    //         return;
    //     }

    //     const meta = methodData.meta as CredPaymentMethodMeta;
    //     const message = credEligibleData?.isOfferLoading ? (
    //         <DynamicPromoSubText>
    //             {meta.loadingOfferMessage ?? LOADING_OFFER_CTA} <ThreeDotAnimation />
    //         </DynamicPromoSubText>
    //     ) : (
    //         <DynamicPromoSubText>{credEligibleData?.bannerText}</DynamicPromoSubText>
    //     );
    //     return message;
    // }, [methodData, credEligibleData]);

    const onPay = useCallback(
        (extraData = {}, cvv?: string): void => {
            //PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT is made undefined as swiggy money is not there in
            // for turbopayment currently
            const generalExtraParams = {
                [PLACE_ORDER_EXTRA_PARAMS.SWIGGY_PAY_AMOUNT]: undefined,
            };

            let paymentMethodExtraParams;

            if (isWalletPaymentMethod(methodData)) {
                paymentMethodExtraParams = getWalletPaymentMethodExtraParams(
                    methodData,
                    paymentActions as WalletPaymentGroupActions,
                );
            }

            if (isCardPaymentMethod(methodData)) {
                paymentMethodExtraParams = getCardPaymentMethodExtraParams(methodData, cvv);
            }

            if (isInAppUpiPaymentMethod(methodData)) {
                paymentMethodExtraParams = getInAppPaymentMethodExtraParams(methodData);
            }

            paymentActions.onPay(
                methodData,
                { ...generalExtraParams, ...paymentMethodExtraParams },
                {
                    isTurboPaymentMethod: true,
                    ...extraData,
                },
            );
        },
        [methodData, paymentActions],
    );

    const onPayButtonSlide = useCallback(
        (sliderPosition, containerWidth) => {
            onSlideButtonSuccess?.(sliderPosition, containerWidth);

            if (isCardPaymentMethod(methodData) && !isFoodCard(methodData)) {
                // CVV not required for sodexo cards, one click Cards
                // Passing cardData  as DEFAULT_CARD_DATA since food card is excluded
                const isCvvRequired = getCvvRequired(methodData, DEFAULT_CARD_DATA_STATE);
                if (isCvvRequired) {
                    setShowCvvPopup(true);
                    return;
                }
            }

            if (isCODPaymentMethod(methodData)) {
                // cod consent on turbo widget
                const metadata = methodData.meta as TurboCodPaymentMethodMeta;

                if (!isEmpty(metadata.halfCard) && !isEmpty(metadata.halfCardInfo)) {
                    setPodHalfCard(true);
                    return;
                }
            }
            onPay({ slidePercent: (sliderPosition / containerWidth) * 100 });
        },
        [methodData, onPay, onSlideButtonSuccess],
    );

    const onCloseCvvBs = useCallback(() => {
        /* on dismissing CVV popup we need to close the popup and reset the slider */
        setShowCvvPopup(false);
        onPostSliderSuccessCallback();
    }, [onPostSliderSuccessCallback]);

    const onCardPayAction = useCallback(
        (cvv: string) => {
            setShowCvvPopup(false);
            // since this CVV popup will only be shown at end of slide payment, slide percent will be 100
            onPay({ slidePercent: 100 }, cvv);
        },
        [onPay],
    );

    const slideToPayCtaPrefix = useMemo(() => {
        if (isCODPaymentMethod(methodData)) {
            const metadata = methodData.meta as TurboCodPaymentMethodMeta;
            return metadata.ctaText ?? DEFAULT_SLIDE_TO_PAY_PREFIX[VALID_PAYMENT_METHODS.COD];
        }
        return SLIDE_TO_PAY_PREFIX;
    }, [methodData]);

    const onTurboPodHalfCardPay = useCallback(() => {
        setPodHalfCard(false);
        onPay({ slidePercent: 100 });
    }, [onPay]);

    const hideTurboPodHalfCard = useCallback(() => {
        setPodHalfCard(false);
        onPostSliderSuccessCallback();
    }, [onPostSliderSuccessCallback]);

    const renderTurboChangeButton = useMemo(() => {
        if (showChangeAnimation) {
            return (
                <ChangePaymentCtaV2
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                    onClick={onClickChangePaymentCta}
                />
            );
        } else {
            return (
                <ChangePaymentCta
                    dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.CHANGE_CTA}
                    onClick={onClickChangePaymentCta}
                />
            );
        }
    }, [onClickChangePaymentCta, showChangeAnimation]);

    useEffectOnce(() => {
        if (showTurboStrip && !isEmpty(turboStrip.bannerId)) {
            updateFrequencyCount(CappingModes.VIEW, turboStrip.bannerId || "");
        }
    });

    return (
        <>
            {showTurboStrip && (
                <TurboStrip
                    onClickTurboStrip={onClickTurboStrip}
                    onImpressionTurboStrip={onImpressionTurboStrip}
                    data={turboStrip}
                    isTurboV2={isTurboV2}
                />
            )}
            <Container>
                <PaymentMethodContainer>
                    <TurboPaymentMethodIcon src={iconUrl} />
                    <PrimaryContainer>
                        <DisplayNameContainer>
                            <TurboPaymentDisplayName
                                displayName={methodDisplayName}
                                superScriptText={showPayUsingSuperScript ? PAY_USING : ""}
                                inlineBalanceMeta={showInlineBalance ? balanceMeta : undefined}
                                cardSubName={cardSubName}
                            />
                            {renderTurboChangeButton}
                        </DisplayNameContainer>
                        <TurboPaymentMethodMetaWidget
                            promoMsg={promoMsg}
                            discount={discount}
                            newLineBalanceMeta={showNewLineBalance ? balanceMeta : undefined}
                            clampDiscountText={clampDiscountText}
                        />
                    </PrimaryContainer>
                </PaymentMethodContainer>
                <PaymentMethodSlideButtonContainer>
                    <SlideButton
                        dataTestId={TURBO_PAYMENT_METHOD_WIDGET_DATA_TEST_IDS.SLIDER_BUTTON}
                        sliderButtonRef={sliderButtonRef}
                        showInitialBumpAnimation={!isBumpAnimationShown}
                        onDragUpdate={throttle(onSlideButtonDrag, SLIDE_UPDATE_THROTTLE_TIME)}
                        onSuccess={onPayButtonSlide}
                        prefixText={slideToPayCtaPrefix}
                        amountText={format(SLIDE_TO_PAY_AMOUNT, turboPaymentCtaMeta?.amountWithDiscount || "")}
                        discountSubtext={
                            showDiscountedAmount && turboPaymentCtaMeta?.amountWithoutDiscount
                                ? format(SLIDE_TO_PAY_AMOUNT, turboPaymentCtaMeta.amountWithoutDiscount)
                                : undefined
                        }
                        showSubtextForInstantFoP={toShowInstantPaymentSubtext}
                        increaseSliderWidth={increaseSliderWidth}
                    />
                </PaymentMethodSlideButtonContainer>
                {showCvvPopup && (
                    <CvvBottomSheet
                        cardSubName={cardSubName}
                        methodData={methodData as CardPaymentMethodData}
                        onCloseCvvBs={onCloseCvvBs}
                        onCardPayAction={onCardPayAction}
                    ></CvvBottomSheet>
                )}
                {showPodHalfCard && (
                    <PodConsentHalfCard
                        isActive={showPodHalfCard}
                        data={methodData.meta as TurboCodPaymentMethodMeta}
                        onPlaceOrder={onTurboPodHalfCardPay}
                        onCancel={hideTurboPodHalfCard}
                        turboPodHalfCardEvents={turboPodHalfCardEvents}
                    />
                )}
            </Container>
            {showKeyFactsDisclaimer && (
                <KfsDisclaimerContainerV1>
                    <Text variant="V2.overline.neutral.regular" color="text_Med_Emphasis">
                        {TEXTS.KEY_FACTS_DISCLAIMER_TURBO}
                        <KfsLinkV1 role="link" tabIndex={0} onClick={handleKeyFactsClick}>
                            {TEXTS.KEY_FACTS_STATEMENT}
                        </KfsLinkV1>
                        .
                    </Text>
                </KfsDisclaimerContainerV1>
            )}
        </>
    );
}
export default memo(TurboPaymentMethodWidget);
